import React, { forwardRef, useMemo } from 'react';
import { Box, Alert, CircularProgress } from '@mui/material';
import { useMapProvider } from './MapProvider';
import { UniversalMapProps, LatLng, UniversalMapEntry } from './MapInterface';
import GoogleMapComponent from './GoogleMapComponent';
import MapboxMapComponent from './MapboxMapComponent';

/**
 * Universal Map Component
 * 
 * Provides a unified API that automatically switches between Google Maps and Mapbox
 * based on the admin-selected provider. Maintains backwards compatibility with 
 * existing Google Maps components.
 */
const UniversalMap = forwardRef<HTMLDivElement, UniversalMapProps>(({
  center,
  zoom,
  className,
  style,
  onMapClick,
  onLocationSearch,
  onMarkerClick,
  markers = [],
  entries = [],
  route,
  showSearch = true,
  showControls = true,
  interactive = true,
  disabled = false,
  ...props
}, ref) => {
  const { provider, isLoaded, error } = useMapProvider();

  // Memoize the common props to prevent unnecessary re-renders
  const commonProps = useMemo(() => ({
    center,
    zoom,
    className,
    style,
    onMapClick,
    onLocationSearch,
    onMarkerClick,
    markers,
    entries,
    route,
    showSearch,
    showControls,
    interactive: interactive && !disabled,
    ...props
  }), [
    center, zoom, className, style, onMapClick, onLocationSearch, 
    onMarkerClick, markers, entries, route, showSearch, showControls, 
    interactive, disabled, props
  ]);

  // Show loading state while provider context is initializing
  if (!isLoaded) {
    return (
      <Box 
        ref={ref}
        className={className}
        style={style}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: 400,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <span>Loading map...</span>
        </Box>
      </Box>
    );
  }

  // Show error state if provider context failed to load
  if (error) {
    return (
      <Box ref={ref} className={className} style={style}>
        <Alert severity="error">
          Map Error: {error}
        </Alert>
      </Box>
    );
  }

  // Render the appropriate map component based on admin-selected provider
  switch (provider) {
    case 'mapbox':
      return (
        <MapboxMapComponent
          ref={ref}
          {...commonProps}
        />
      );
    
    case 'google':
    default:
      return (
        <GoogleMapComponent
          ref={ref}
          {...commonProps}
        />
      );
  }
});

UniversalMap.displayName = 'UniversalMap';

export default UniversalMap;

// Helper function to convert Google Maps LatLngLiteral to our LatLng interface
export const convertGoogleMapsLatLng = (googleLatLng: google.maps.LatLngLiteral): LatLng => ({
  lat: googleLatLng.lat,
  lng: googleLatLng.lng
});

// Helper function to convert TravelEntry to UniversalMapEntry
export const convertTravelEntryToMapEntry = (entry: any): UniversalMapEntry => ({
  id: entry.id,
  title: entry.title || entry.notes?.substring(0, 50) || 'Untitled Entry',
  location: {
    lat: entry.location?.coordinates?.[1] || entry.latitude || 0,
    lng: entry.location?.coordinates?.[0] || entry.longitude || 0
  },
  locationName: entry.locationName || entry.location_name,
  content: entry.notes,
  date: entry.createdAt || entry.created_at || entry.date,
  ...entry // Preserve all original properties
});

// Helper function to create map markers from entries
export const createMarkersFromEntries = (entries: UniversalMapEntry[]): Array<{ position: LatLng; title: string }> => {
  return entries.map(entry => ({
    position: entry.location,
    title: entry.title
  }));
};